import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

export const Navbar = () => {
  const { t, toggle, lang, other } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#shop", label: t.nav.shop },
    { href: "#quality", label: t.nav.quality },
    { href: "#wholesale", label: t.nav.wholesale },
    { href: "#about", label: t.nav.about },
    { href: "#reseller", label: t.nav.reseller },
    { href: "#contact", label: t.nav.contact },
  ];

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-brand-cream/85 backdrop-blur-xl border-b border-brand-ink/5" : "bg-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <a href="#top" className="flex flex-col leading-none group" data-testid="brand-logo">
          <span className="font-serif italic text-[10px] tracking-[0.3em] text-brand-red">est. 1972</span>
          <span className="font-serif text-xl md:text-2xl tracking-tight text-brand-ink mt-1">
            BANDENG <span className="italic text-brand-red">Primaroso</span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="font-sans text-sm tracking-wide text-brand-ink/80 hover:text-brand-red transition-colors relative group"
              data-testid={`nav-link-${l.href.replace("#", "")}`}
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-brand-red group-hover:w-full transition-all duration-500" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            className="flex items-center gap-2 px-3 py-1.5 border border-brand-ink/20 rounded-full text-xs font-sans tracking-[0.15em] hover:border-brand-red hover:text-brand-red transition-colors"
            data-testid="lang-toggle"
            aria-label="Toggle language"
          >
            <Globe className="w-3.5 h-3.5" />
            <span className="font-medium">{lang.toUpperCase()}</span>
            <span className="text-brand-ink/30">/</span>
            <span className="text-brand-ink/40">{other}</span>
          </button>
          <button
            onClick={() => setOpen(true)}
            className="lg:hidden w-10 h-10 flex items-center justify-center border border-brand-ink/20 rounded-full hover:border-brand-red hover:text-brand-red transition-colors"
            data-testid="mobile-menu-open"
            aria-label="Open menu"
          >
            <Menu className="w-4 h-4" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-brand-cream lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex justify-end p-6">
              <button
                onClick={() => setOpen(false)}
                className="w-10 h-10 flex items-center justify-center border border-brand-ink/20 rounded-full"
                data-testid="mobile-menu-close"
                aria-label="Close menu"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <motion.nav
              initial="hidden"
              animate="visible"
              variants={{ visible: { transition: { staggerChildren: 0.07 } } }}
              className="px-8 flex flex-col gap-6 mt-8"
            >
              {links.map((l) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
                  }}
                  className="font-serif text-4xl text-brand-ink border-b border-brand-ink/10 pb-4"
                  data-testid={`mobile-nav-${l.href.replace("#", "")}`}
                >
                  {l.label}
                </motion.a>
              ))}
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
